import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, ArrowLeft, Info, Calendar } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import SurveyService from "../../lib/SurveyService";
import RefinancingSurveyReadOnlySummary from "./RefinancingSurveyReadOnlySummary";

import {
  useSurveyTracking,
  useInteractionTracking,
  useErrorTracking,
} from "../../hooks/useAnalytics";
import RefinanceService from "../../lib/RefinanceService";

const RefinancingSurveyScreen = ({ onComplete, onBack }) => {
  // Authentication and tracking hooks
  const { getIdToken, user } = useAuth();
  const {
    trackSurveyStart,
    trackSurveyQuestion,
    trackSurveyComplete,
  } = useSurveyTracking();
  const { trackButtonClick } = useInteractionTracking();
  const { trackError } = useErrorTracking();
  const CACHE_KEY = `refinancing_survey_answers`;

  // Survey configuration
  const [surveyId] = useState("STEM_YzytO5xwcc");

  // State management
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [surveyData, setSurveyData] = useState(null);
  const [sortedQuestions, setSortedQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Read-only state
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [previousResponses, setPreviousResponses] = useState(null);
  const [renewal, setRenewal] = useState(false);

  // Tracking survey start time
  const startTimeRef = useRef(Date.now());

  const checkPreviousResponses = async (token) => {
    try {
      if (!token) return { hasPreviousResponse: false };

      // Fetch previous responses using the same method as other surveys
      const responseData = await SurveyService.getSurveyResponsesBySurveyId(
        surveyId,
        token
      );

      // If we have completed responses
      if (responseData && responseData.dateCompleted) {
        setPreviousResponses(responseData);
        return { hasPreviousResponse: true, previousResponses: responseData };
      }

      return { hasPreviousResponse: false };
    } catch (err) {
      console.log("No previous responses found or error checking:", err);
      return { hasPreviousResponse: false };
    }
  };

  // Load cached answers on mount
  useEffect(() => {
    const cachedAnswers = localStorage.getItem(CACHE_KEY);
    if (cachedAnswers) {
      try {
        const parsed = JSON.parse(cachedAnswers);
        setAnswers(parsed);
      } catch (error) {
        console.error("Error parsing cached answers:", error);
      }
    }
  }, []);

  // Save answers to local storage whenever they change
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(answers));
    }
  }, [answers]);

  // Fetch survey data on component mount
  useEffect(() => {
    const fetchSurvey = async () => {
      let token = null;
      try {
        setIsLoading(true);
        let previousResponses = { hasPreviousResponse: false };
        // Get authentication token
        // Check for previous responses if user is authenticated
        try {
          token = await getIdToken();
          if (user && token) {
            previousResponses = await checkPreviousResponses(token);
            // Set read-only mode if we have previous responses
            if (
              previousResponses.hasPreviousResponse &&
              previousResponses.previousResponses?.dateCompleted
            ) {
              setIsReadOnly(true);
            }
          }
        } catch (e) {
          //console.warn("No authentication token available:", e);
        }
        // Fetch survey details
        const surveyTemplate = await SurveyService.getSurveyById(
          surveyId,
          token
        );
        // Sort questions by display order
        const questions = [...surveyTemplate.questions].sort(
          (a, b) => a.displayOrder - b.displayOrder
        );
        setSurveyData(surveyTemplate);
        setSortedQuestions(questions);
        // Track survey start
        trackSurveyStart(surveyTemplate.title || "refinancing_survey");
        await createFlow();
        setIsLoading(false);
      } catch (err) {
        if (token && user) {
          console.log("Error loading survey:", err);
          //trackError("survey_load_error", err.message);
          setErrors((prev) => ({
            ...prev,
            general: "Failed to load survey questions. Please try again.",
          }));
          setIsLoading(false);
        }
      }
    };

    const createFlow = async () => {
      try {
        if (user) {
          console.log("qualifieds");
          const token = await getIdToken();
          console.log(token);
          if (!token) throw new Error("Authentication required");
          const flowData = await RefinanceService.createFlow(token);
          sessionStorage.setItem("refinancingFlowId", flowData.id);
        }
      } catch (err) {
        console.error("Error loading Extended Warranty flow:", err);
      }
    };

    if (surveyId) {
      fetchSurvey();
    }
  }, [surveyId, getIdToken, user]);

  // Validation function for current question
  const validateCurrentQuestion = () => {
    console.log("VALIDATECURRENT");
    const currentQuestion = sortedQuestions[currentQuestionIndex];
    const currentAnswer = answers[currentQuestion.id];

    // Clear previous errors
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[currentQuestion.id];
      return newErrors;
    });

    // Skip validation if not required
    if (!currentQuestion.required) return true;

    // Validate TEXT questions
    if (currentQuestion.type === "TEXT") {
      if (!currentAnswer || currentAnswer.value.trim() === "") {
        setErrors((prev) => ({
          ...prev,
          [currentQuestion.id]: "Please provide an answer",
        }));
        return false;
      }

      // Additional numeric validation for numeric text fields
      if (
        currentQuestion.questionType === "NUMERIC" ||
        currentQuestion.questionType === "MONEY"
      ) {
        const numValue = parseFloat(currentAnswer.value);
        if (
          isNaN(numValue) ||
          currentAnswer.value.trim().replace(/[0-9.]/g, "") !== ""
        ) {
          setErrors((prev) => ({
            ...prev,
            [currentQuestion.id]: "Please enter a valid number",
          }));
          return false;
        }
      }
    }

    // Validate CHOICE questions
    if (currentQuestion.type === "CHOICE") {
      if (!currentAnswer || !currentAnswer.value) {
        setErrors((prev) => ({
          ...prev,
          [currentQuestion.id]: "Please select an option",
        }));
        return false;
      }
    }

    return true;
  };

  // Handle answer input for different question types
  const handleAnswer = (questionId, value, optionId = null) => {
    const currentQuestion = sortedQuestions[currentQuestionIndex];

    let answerObject;
    if (currentQuestion.type === "CHOICE") {
      // Find the selected option
      const selectedOption = currentQuestion.options.find(
        (opt) => opt.id === optionId
      );
      answerObject = {
        value: selectedOption.value,
        answerId: optionId,
        text: selectedOption.text,
        questionId,
      };
    } else {
      // TEXT questions
      answerObject = {
        value: value,
        questionId,
      };
    }

    setAnswers((prev) => ({
      ...prev,
      [questionId]: answerObject,
    }));

    // Track question interaction
    trackSurveyQuestion(currentQuestion.prompt);
  };

  // Navigate to next question
  const handleNextQuestion = () => {
    // Validate current question
    if (!validateCurrentQuestion()) return;

    // Move to next question or complete survey
    if (currentQuestionIndex < sortedQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleComplete();
    }
  };

  // Navigate to previous question
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    } else {
      onBack();
    }
  };

  const formatMoney = (value) => {
    if (!value) return "";
    // Remove all non-digits
    const digits = value.replace(/[^0-9]/g, "");
    // Add commas
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getMaxAdultDate = () => {
    const today = new Date();
    const adult = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate()
    );
    return adult.toISOString().split("T")[0]; // "YYYY-MM-DD"
  };

  // Date dropdown helpers
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 18; // Must be 18+
  const minYear = currentYear - 100; // 100 years back

  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(year, parseInt(month), 0).getDate();
  };

  const parseDateValue = (value) => {
    if (!value) return { year: "", month: "", day: "" };
    const parts = value.split("-");
    return {
      year: parts[0] || "",
      month: parts[1] || "",
      day: parts[2] || "",
    };
  };

  const handleDatePartChange = (questionId, part, partValue, currentValue) => {
    const current = parseDateValue(currentValue);
    current[part] = partValue;

    // Adjust day if it exceeds days in selected month
    if (current.year && current.month && current.day) {
      const maxDays = getDaysInMonth(current.year, current.month);
      if (parseInt(current.day) > maxDays) {
        current.day = String(maxDays).padStart(2, "0");
      }
    }

    const newValue = `${current.year}-${current.month}-${current.day}`;

    handleAnswer(questionId, newValue);
  };

  // Complete survey submission
  const handleComplete = async () => {
    try {
      setIsLoading(true);
      console.log("isreadonly=" + isReadOnly);
      // 🔥 PERSISTENCE: In read-only mode, pass previous responses
      if (isReadOnly && previousResponses) {
        onComplete({
          surveyAnswers: answers,
          completedAt: previousResponses.dateCompleted,
          readOnly: true,
          previousResponses: previousResponses.responses,
        });
        return;
      }

      // Validate all required questions
      const requiredQuestions = sortedQuestions.filter((q) => q.required);
      const allQuestionsValid = requiredQuestions.every(
        (q) => answers[q.id] && (!q.required || answers[q.id].value)
      );

      if (!allQuestionsValid) {
        setErrors((prev) => ({
          ...prev,
          general: "Please answer all required questions",
        }));
        setIsLoading(false);
        return;
      }

      // Format responses for API submission
      const formattedResponses = sortedQuestions.map((question) => {
        const answer = answers[question.id];

        if (question.type === "CHOICE") {
          return {
            type: "CHOICE",
            questionId: question.id,
            selections: [
              {
                choiceOptionId: answer.answerId,
                value: answer.value,
              },
            ],
          };
        }

        return {
          type: "TEXT",
          questionId: question.id,
          answer: answer.value || "",
        };
      });

      // Track survey completion
      const completionTime = Date.now() - startTimeRef.current;
      trackSurveyComplete(
        surveyData?.title || "refinancing_survey",
        sortedQuestions.length,
        completionTime
      );

      // Submit survey (if authenticated)
      if (user) {
        const token = await getIdToken();
        await SurveyService.submitSurveyResponsesForRefinancing(
          surveyId,
          user.uid,
          formattedResponses,
          token
        );
      }

      // Call completion handler
      onComplete({
        surveyAnswers: answers,
        formattedResponses,
        completedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("Survey submission error:", err);
      trackError("survey_submission_error", err.message);
      setErrors((prev) => ({
        ...prev,
        general: "Failed to submit survey. Please try again.",
      }));
      setIsLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <span className="ml-3">Synchronizing survey data...</span>
      </div>
    );
  }

  // Error state
  if (errors.general) {
    return (
      <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-red-800">
        <h3 className="font-medium">Error</h3>
        <p>{errors.general}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-red-600 hover:text-red-800"
        >
          Retry
        </button>
      </div>
    );
  }

  // No questions available
  if (sortedQuestions.length === 0) {
    return <div>No survey questions available</div>;
  }

  // Get the current question
  const currentQuestion = sortedQuestions[currentQuestionIndex];

  // Calculate progress
  const progress = ((currentQuestionIndex + 1) / sortedQuestions.length) * 100;

  return (
    <div className="p-4 space-y-6">
      {/* Read-only banner */}
      {isReadOnly && previousResponses ? (
        <div className="p-4 rounded-md">
          <p className="text-sm text-blue-700">
            This survey was completed on{" "}
            {new Date(previousResponses.dateCompleted).toLocaleDateString()}.{" "}
          </p>
          {/* Show extracted summary component */}
          <RefinancingSurveyReadOnlySummary
            sortedQuestions={sortedQuestions}
            previousResponses={previousResponses}
            onContinue={handleComplete}
          />
        </div>
      ) : (
        <>
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
            <div
              className="bg-purple-600 h-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Step indicator */}
          <div className="flex justify-between text-sm text-gray-600 px-1">
            <span>
              Question {currentQuestionIndex + 1} of {sortedQuestions.length}
            </span>
            <span>{Math.round(progress)}% Complete</span>
          </div>

          {/* Survey Title */}
          <div className="text-center mb-6">
            <h2 className="text-xl uppercase font-bold text-gray-900">
              Refinancing Survey
            </h2>
            <p className="text-darkGrey mt-4">
              Help us understand your financial situation
            </p>
          </div>

          {/* Current Question */}
          <div className="space-y-6">
            <div className="!mb-10">
              <div className="mb-8">
                <span className="font-bold text-base">
                  {currentQuestionIndex + 1}. {currentQuestion.prompt}
                </span>
                {currentQuestion.helpText && (
                  <div className="text-sm text-gray-500 mt-1">
                    {currentQuestion.helpText}
                  </div>
                )}
              </div>

              {/* Question Rendering */}
              <div className="flex flex-col gap-8 ml-1">
                {/* CHOICE Question Type */}
                {currentQuestion.type === "CHOICE" && (
                  <>
                    {[...currentQuestion.options]
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((option) => {
                        const selected =
                          answers[currentQuestion.id]?.value === option.value;
                        // If in read-only mode, determine if this was the previous answer
                        const isPreviousAnswer =
                          isReadOnly &&
                          previousResponses?.responses?.find(
                            (resp) =>
                              resp.questionId === currentQuestion.id &&
                              resp.selections[0].value === option.value
                          );

                        return (
                          <div
                            key={option.id}
                            onClick={() =>
                              !isReadOnly &&
                              handleAnswer(
                                currentQuestion.id,
                                option.value,
                                option.id
                              )
                            }
                            className="flex items-center gap-3"
                          >
                            <span
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                selected || isPreviousAnswer
                                  ? "border-violet-500"
                                  : "border-gray-400"
                              } bg-white`}
                            >
                              {(selected || isPreviousAnswer) && (
                                <span className="w-3 h-3 rounded-full bg-violet-500 block" />
                              )}
                            </span>
                            <span
                              className={`text-base ${
                                selected || isPreviousAnswer
                                  ? "text-violet-500"
                                  : "text-black"
                              }`}
                            >
                              {option.text}
                              {isReadOnly && isPreviousAnswer && (
                                <span className="ml-2 text-sm text-gray-500">
                                  (Previous Answer)
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                  </>
                )}

                {/* TEXT Question Type */}
                {/* TEXT Question Type */}
                {currentQuestion.type === "TEXT" && (
                  <>
                    {currentQuestion.questionType === "DATE" ? (
                      (() => {
                        const dateValue = isReadOnly
                          ? previousResponses?.responses?.find(
                              (resp) => resp.questionId === currentQuestion.id
                            )?.answer || ""
                          : answers[currentQuestion.id]?.value || "";
                        const { year, month, day } = parseDateValue(dateValue);
                        const daysInMonth = getDaysInMonth(year, month);
                        const days = Array.from({ length: daysInMonth }, (_, i) =>
                          String(i + 1).padStart(2, "0")
                        );

                        return (
                          <div className="flex gap-3">
                            {/* Month */}
                            <select
                              value={month}
                              disabled={isReadOnly}
                              onChange={(e) =>
                                handleDatePartChange(
                                  currentQuestion.id,
                                  "month",
                                  e.target.value,
                                  dateValue
                                )
                              }
                              className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none bg-gray-100"
                            >
                              <option value="">Month</option>
                              {months.map((m) => (
                                <option key={m.value} value={m.value}>
                                  {m.label}
                                </option>
                              ))}
                            </select>

                            {/* Day */}
                            <select
                              value={day}
                              disabled={isReadOnly}
                              onChange={(e) =>
                                handleDatePartChange(
                                  currentQuestion.id,
                                  "day",
                                  e.target.value,
                                  dateValue
                                )
                              }
                              className="w-20 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none bg-gray-100"
                            >
                              <option value="">Day</option>
                              {days.map((d) => (
                                <option key={d} value={d}>
                                  {parseInt(d)}
                                </option>
                              ))}
                            </select>

                            {/* Year */}
                            <select
                              value={year}
                              disabled={isReadOnly}
                              onChange={(e) =>
                                handleDatePartChange(
                                  currentQuestion.id,
                                  "year",
                                  e.target.value,
                                  dateValue
                                )
                              }
                              className="w-24 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none bg-gray-100"
                            >
                              <option value="">Year</option>
                              {years.map((y) => (
                                <option key={y} value={y}>
                                  {y}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })()
                    ) : currentQuestion.questionType === "MONEY" ? (
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          $
                        </div>
                        <input
                          type="text"
                          value={
                            isReadOnly
                              ? previousResponses?.responses?.find(
                                  (resp) =>
                                    resp.questionId === currentQuestion.id
                                )?.answer || ""
                              : formatMoney(
                                  answers[currentQuestion.id]?.value || ""
                                )
                          }
                          readOnly={isReadOnly}
                          onChange={(e) => {
                            if (isReadOnly) return;

                            const rawDigits = e.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                            handleAnswer(currentQuestion.id, rawDigits);
                          }}
                          className="w-full pl-6 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none bg-gray-100"
                        />
                      </div>
                    ) : currentQuestion.questionType === "NUMERIC" ? (
                      <input
                        type="number"
                        value={
                          isReadOnly
                            ? previousResponses?.responses?.find(
                                (resp) => resp.questionId === currentQuestion.id
                              )?.answer || ""
                            : answers[currentQuestion.id]?.value || ""
                        }
                        readOnly={isReadOnly}
                        // Add onChange handler
                        onChange={(e) =>
                          !isReadOnly &&
                          handleAnswer(currentQuestion.id, e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none bg-gray-100"
                      />
                    ) : (
                      <input
                        type="text"
                        value={
                          isReadOnly
                            ? previousResponses?.responses?.find(
                                (resp) => resp.questionId === currentQuestion.id
                              )?.answer || ""
                            : answers[currentQuestion.id]?.value || ""
                        }
                        readOnly={isReadOnly}
                        // Add onChange handler
                        onChange={(e) =>
                          !isReadOnly &&
                          handleAnswer(currentQuestion.id, e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none bg-gray-100"
                      />
                    )}
                  </>
                )}
              </div>
              {/* Error Display */}
              {errors[currentQuestion.id] && (
                <div className="mt-4 text-red-500 text-sm">
                  {errors[currentQuestion.id]}
                </div>
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col space-y-4">
            {/* Next/Complete button */}
            <button
              onClick={handleNextQuestion}
              className={`w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors ${
                !answers[currentQuestion.id] && !isReadOnly
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              disabled={!answers[currentQuestion.id] && !isReadOnly}
            >
              {currentQuestionIndex === sortedQuestions.length - 1
                ? isReadOnly
                  ? "Completed"
                  : "Complete"
                : isReadOnly
                ? "Next"
                : "Next"}
              {currentQuestionIndex < sortedQuestions.length - 1 &&
                !isReadOnly && <ArrowRight className="w-4 h-4" />}
            </button>

            {/* Back button */}
            {(!isReadOnly || currentQuestionIndex > 0) && (
              <button
                onClick={handlePreviousQuestion}
                className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors"
              >
                {currentQuestionIndex > 0 ? "Previous" : "Back"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RefinancingSurveyScreen;
